import type { ReactNode } from 'react';
import { Panel } from './ui/Panel';
import { Button } from './ui/Button';
import { ProgressBar } from './ui/ProgressBar';
import DarkMatterAmount from './DarkMatterAmount';

interface QueueItemCardProps {
    /** The `<img>`/badge visual — must fill a `w-full h-full` box. Dimmed by this component. */
    image: ReactNode;
    /** Overlaid centered on the (dimmed) image, e.g. `Level 5` or `× 3`. */
    overlayLabel: string;
    name: string;
    /** Countdown/status text, e.g. "Upgrading… 2:30" — rendered below the card. */
    statusText: string;
    /** 0..1 build-timer progress. Omit to hide the bar (e.g. an item still waiting behind another). */
    progress?: number;
    error?: string | null;
    /** Extra line under `statusText`, e.g. what cancelling this item would cost. Styled as a caveat, not an error. */
    footnote?: string | null;
    isCancelling: boolean;
    /** When true, Cancel is disabled regardless of `isCancelling` — e.g. a batch the server will no longer accept a cancellation for. */
    cancelDisabled?: boolean;
    /** Omit to hide Cancel entirely — e.g. a viewer who may watch a shared queue but not change it. */
    onCancel?: () => void;
    /** Width + aspect-ratio classes for the image box. Defaults to the
     * portrait shape building sprites were authored at; ships/badges pass a
     * different box instead. */
    imageBoxClassName?: string;
    /** Dark Matter price to instantly complete this item — pass to show a "Rush" button alongside Cancel. Omit to hide it entirely (e.g. a non-rushable science). */
    rushCost?: number;
    isRushing?: boolean;
    /** When true, Rush is disabled regardless of `isRushing` — e.g. not enough Dark Matter, or already rushed. */
    rushDisabled?: boolean;
    /** Replaces the "Rush (N Dark Matter)" label/tooltip with this when `rushDisabled` is true for a specific, nameable reason (e.g. "Rushed" once its own duration is already zero) — omit to fall back to the plain cost label (e.g. an affordability-only disable). */
    rushDisabledReason?: string;
    onRush?: () => void;
}

/**
 * One queued/in-progress item (building upgrade, research, ship upgrade, or
 * ship batch build) rendered as its own info-toned panel — visually modeled
 * on `UnmetRequirementsGrid`'s danger-toned tiles, but info-toned so the two
 * concepts (missing prerequisite vs. already queued) never look the same.
 */
export default function QueueItemCard({
    image,
    overlayLabel,
    name,
    statusText,
    progress,
    error,
    footnote,
    isCancelling,
    cancelDisabled = false,
    onCancel,
    imageBoxClassName = 'w-20 aspect-[3/4]',
    rushCost,
    isRushing = false,
    rushDisabled = false,
    rushDisabledReason,
    onRush,
}: QueueItemCardProps) {
    return (
        <div className="flex flex-col items-center gap-1.5 w-24 shrink-0" data-testid="queue-item-card">
            <Panel
                variant="solid"
                padding="sm"
                className="flex flex-col items-center gap-1.5 border-status-info/40 hover:border-status-info transition-colors w-full"
            >
                <div className={`relative ${imageBoxClassName}`}>
                    <div className="w-full h-full opacity-50 grayscale">{image}</div>
                    <span className="absolute inset-0 flex items-center justify-center rounded-control bg-space-950/50 text-status-info text-label font-bold text-center px-0.5">
                        {overlayLabel}
                    </span>
                </div>
                <span className="text-label text-status-info font-semibold text-center leading-tight truncate w-full" title={name}>
                    {name}
                </span>
                {progress !== undefined && <ProgressBar value={progress} accent="zone" className="w-full" />}
                {onRush && rushCost !== undefined && (
                    <Button
                        variant="accent"
                        size="sm"
                        className="w-full h-auto min-h-7 flex-col gap-0 py-1 leading-tight truncate"
                        onClick={onRush}
                        disabled={isRushing || rushDisabled}
                        title={rushDisabledReason ?? `Instantly complete for ${rushCost} Dark Matter`}
                        aria-label={rushDisabledReason ?? `Rush for ${rushCost} Dark Matter`}
                    >
                        {isRushing ? 'Rushing…' : rushDisabledReason ?? (
                            <>
                                <span>Rush</span>{' '}
                                <DarkMatterAmount amount={rushCost} />
                            </>
                        )}
                    </Button>
                )}
                {onCancel && (
                    <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={onCancel}
                        disabled={isCancelling || cancelDisabled}
                    >
                        {isCancelling ? 'Cancelling…' : 'Cancel'}
                    </Button>
                )}
            </Panel>
            <span className="text-micro text-content-muted text-center leading-tight">{statusText}</span>
            {footnote && <span className="text-micro text-status-warning text-center leading-tight">{footnote}</span>}
            {error && (
                <span className="text-micro text-status-danger text-center" role="alert">
                    {error}
                </span>
            )}
        </div>
    );
}
